/**
 * Math Editor Functions
 * Handles math modal UI and math insertion
 */

import katex from 'katex'

const mathDrawingInstances = new Map()
const recognizedLatex = new Map()
const mathEditPositions = new Map() // modalId -> {pos, mathType}

/**
 * Show the math modal
 * @param {string} modalId - The ID of the modal element
 * @param {string} type - 'inline' or 'block'
 * @param {string} initialLatex - Optional initial LaTeX to pre-fill in the textarea
 * @param {number} editPos - Optional position of math node being edited
 */
export function showMathModal(
  modalId,
  type = 'block',
  initialLatex = '',
  editPos = null
) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  const radioName = `math_type_${modalId.replace('math_modal_', '')}`
  const radio = modal.querySelector(
    `input[type="radio"][name="${radioName}"][value="${type}"]`
  )
  if (radio) {
    radio.checked = true
  }

  if (editPos !== null) {
    mathEditPositions.set(modalId, { pos: editPos, mathType: type })
  } else {
    mathEditPositions.delete(modalId)
  }

  const textarea = modal.querySelector('textarea')
  if (textarea) {
    textarea.value = initialLatex || ''
    if (initialLatex) {
      setTimeout(() => {
        textarea.setSelectionRange(initialLatex.length, initialLatex.length)
      }, 0)
    }
  }

  updateMathPreview(modalId)

  const instance = mathDrawingInstances.get(modalId)
  if (instance) {
    instance.resetDrawing()
  }

  recognizedLatex.delete(modalId)

  const toolbarInstanceId = modalId.replace('math_modal_', '')
  const previewId = `math_drawing_preview_${toolbarInstanceId}`
  const preview = document.getElementById(previewId)
  if (preview) {
    preview.innerHTML =
      '<div class="math-modal-preview-placeholder">Draw math and click "Recognize" to see preview...</div>'
  }

  const recognizeButton = modal.querySelector(
    '[data-action="recognizeMathDrawing"]'
  )
  const insertButton = modal.querySelector(
    '[data-action="insertMathFromModal"]'
  )

  const drawingTab = modal.querySelector('.math-modal-tab[data-tab="drawing"]')
  if (drawingTab && !initialLatex) {
    drawingTab.click()
    if (recognizeButton)
      recognizeButton.classList.remove('math-modal-button-hidden')
    if (insertButton) insertButton.classList.add('math-modal-button-hidden')
  } else {
    const latexTab = modal.querySelector('.math-modal-tab[data-tab="latex"]')
    if (latexTab) {
      latexTab.click()
    }
    if (recognizeButton)
      recognizeButton.classList.add('math-modal-button-hidden')
    if (insertButton) {
      insertButton.classList.remove('math-modal-button-hidden')
      insertButton.textContent = 'Insert'
    }
  }

  modal.classList.add('is-open')

  if (textarea) {
    setTimeout(() => textarea.focus(), 100)
  }
}

/**
 * Hide the math modal
 * @param {string} modalId - The ID of the modal element
 */
export function hideMathModal(modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  modal.classList.remove('is-open')
}

/**
 * Insert math from the modal
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} modalId - The ID of the modal element
 */
export function insertMathFromModal(editor, modalId) {
  if (!editor) return

  const modal = document.getElementById(modalId)
  if (!modal) return

  const activeTab = modal.querySelector('.math-modal-tab.math-modal-tab-active')
  const activeTabName = activeTab ? activeTab.getAttribute('data-tab') : 'latex'

  if (activeTabName === 'drawing') {
    const latex = recognizedLatex.get(modalId)
    if (latex) {
      insertRecognizedMath(editor, modalId)
    } else {
      alert('Please recognize your drawing first.')
    }
    return
  }

  const radioName = `math_type_${modalId.replace('math_modal_', '')}`
  const selectedRadio = modal.querySelector(
    `input[type="radio"][name="${radioName}"]:checked`
  )
  const mathType = selectedRadio ? selectedRadio.value : 'inline'

  const textarea = modal.querySelector('textarea')
  const latex = textarea ? textarea.value.trim() : ''

  if (!latex) {
    hideMathModal(modalId)
    return
  }

  const editInfo = mathEditPositions.get(modalId)
  if (editInfo && editInfo.pos !== null && editInfo.pos !== undefined) {
    const { pos } = editInfo
    const { state } = editor.view
    const nodeAtPos = state.doc.nodeAt(pos)

    if (
      nodeAtPos &&
      (nodeAtPos.type.name === 'mathBlock' ||
        nodeAtPos.type.name === 'mathInline' ||
        nodeAtPos.type.name === 'blockMath' ||
        nodeAtPos.type.name === 'inlineMath')
    ) {
      try {
        editor
          .chain()
          .setNodeSelection(pos)
          .updateAttributes(nodeAtPos.type.name, { latex })
          .focus()
          .run()
      } catch (e) {
        editor
          .chain()
          .setNodeSelection(pos)
          .deleteSelection()
          .insertContent({
            type: nodeAtPos.type.name,
            attrs: { latex },
          })
          .focus()
          .run()
      }

      mathEditPositions.delete(modalId)
      hideMathModal(modalId)
      return
    }
  }
  const nodeType =
    mathType === 'block'
      ? editor.schema.nodes.mathBlock || editor.schema.nodes.blockMath
      : editor.schema.nodes.mathInline || editor.schema.nodes.inlineMath

  if (nodeType) {
    editor
      .chain()
      .focus()
      .insertContent({
        type: nodeType.name,
        attrs: { latex },
      })
      .run()
  }

  mathEditPositions.delete(modalId)
  hideMathModal(modalId)
}

/**
 * Recognize math from drawing and show in preview
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} modalId - The ID of the modal element
 */
async function recognizeMathDrawing(editor, modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  const instance = mathDrawingInstances.get(modalId)
  if (!instance) {
    alert('Drawing canvas not initialized. Please try again.')
    return
  }

  const strokes = instance.getStrokes()
  if (!strokes || strokes.length === 0) {
    alert('Please draw something before recognizing.')
    return
  }

  const recognizeButton = modal.querySelector(
    '[data-action="recognizeMathDrawing"]'
  )
  const insertButton = modal.querySelector(
    '[data-action="insertMathFromModal"]'
  )
  const originalText = recognizeButton
    ? recognizeButton.textContent
    : 'Recognize'

  if (recognizeButton) {
    recognizeButton.disabled = true
    recognizeButton.textContent = 'Recognizing...'
  }

  const toolbarInstanceId = modalId.replace('math_modal_', '')
  const previewId = `math_drawing_preview_${toolbarInstanceId}`
  const preview = document.getElementById(previewId)

  try {
    const ink_data = strokes.map((stroke) => ({
      x: stroke.x,
      y: stroke.y,
      p: stroke.p || stroke.x.map(() => 1.0),
    }))

    const response = await fetch('/api/myscript/recognize/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCsrfToken(),
      },
      body: JSON.stringify({ ink_data }),
    })

    const contentType = response.headers.get('content-type')
    const isJson = contentType && contentType.includes('application/json')

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`
      if (isJson) {
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (e) {
          // If JSON parsing fails, use default error message
        }
      } else {
        // If response is HTML (error page), try to get text for debugging
        try {
          const text = await response.text()
          console.error('Non-JSON error response:', text.substring(0, 200))
        } catch (e) {
          // Ignore text parsing errors
        }
      }
      throw new Error(errorMessage)
    }

    if (!isJson) {
      throw new Error('Server returned non-JSON response')
    }

    const data = await response.json()
    let latex = data.latex

    if (!latex) {
      throw new Error('No LaTeX returned from recognition')
    }

    latex = latex
      .trim()
      .replace(/\ufeff/g, '')
      .replace(/\u200b/g, '')

    recognizedLatex.set(modalId, latex)

    if (preview) {
      preview.innerHTML = ''
      try {
        katex.render(latex, preview, {
          throwOnError: false,
          displayMode: true,
        })
      } catch (error) {
        preview.innerHTML = `<div class="math-preview-error">Error: ${
          error.message || 'Invalid LaTeX'
        }</div>`
      }
    }
    if (insertButton) {
      insertButton.classList.remove('math-modal-button-hidden')
    }
    if (recognizeButton) {
      recognizeButton.classList.add('math-modal-button-hidden')
    }
  } catch (error) {
    console.error('Error recognizing math:', error)
    if (preview) {
      preview.innerHTML = `<div class="math-preview-error">Error: ${error.message}</div>`
    }
    alert(`Error recognizing math: ${error.message}`)
  } finally {
    if (recognizeButton) {
      recognizeButton.disabled = false
      recognizeButton.textContent = originalText
    }
  }
}

/**
 * Insert recognized math from drawing
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} modalId - The ID of the modal element
 */
function insertRecognizedMath(editor, modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  const latex = recognizedLatex.get(modalId)
  if (!latex) {
    alert('No recognized math to insert. Please recognize your drawing first.')
    return
  }

  const nodeType =
    editor.schema.nodes.mathBlock || editor.schema.nodes.blockMath

  if (nodeType) {
    editor
      .chain()
      .focus()
      .insertContent({
        type: nodeType.name,
        attrs: { latex },
      })
      .run()
  }

  recognizedLatex.delete(modalId)
  hideMathModal(modalId)
}

/**
 * Get CSRF token from cookies
 */
function getCsrfToken() {
  const name = 'csrftoken'
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}

/**
 * Update math button active state
 * @param {Editor} editor - The TipTap editor instance
 * @param {HTMLElement} button - The button element
 */
export function updateMathButtonState(editor, button) {
  if (!editor || !button) return false
  return editor.isActive('mathInline') || editor.isActive('mathBlock')
}

/**
 * Handle math editor action
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} modalId - The ID of the modal element
 */
export function handleMathEditorAction(editor, modalId) {
  showMathModal(modalId, 'block')
}

/**
 * Update math preview
 * @param {string} modalId - The ID of the modal element
 */
export function updateMathPreview(modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  const textarea = modal.querySelector('textarea')
  const preview = modal.querySelector('.math-modal-preview')
  if (!textarea || !preview) return

  const latex = textarea.value.trim()
  const radioName = `math_type_${modalId.replace('math_modal_', '')}`
  const selectedRadio = modal.querySelector(
    `input[type="radio"][name="${radioName}"]:checked`
  )
  const mathType = selectedRadio ? selectedRadio.value : 'block'

  preview.innerHTML = ''

  if (!latex) {
    preview.innerHTML =
      '<div class="math-modal-preview-placeholder">Preview will appear here...</div>'
    return
  }

  try {
    const displayMode = mathType === 'block'
    katex.render(latex, preview, {
      throwOnError: false,
      displayMode: displayMode,
    })
  } catch (error) {
    preview.innerHTML = `<div class="math-preview-error">Error: ${
      error.message || 'Invalid LaTeX'
    }</div>`
  }
}

/**
 * Insert shortcut into textarea at cursor position
 * @param {string} modalId - The ID of the modal element
 * @param {string} shortcut - The LaTeX shortcut to insert
 */
export function insertMathShortcut(modalId, shortcut) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  const textarea = modal.querySelector('textarea')
  if (!textarea) return

  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const text = textarea.value
  const before = text.substring(0, start)
  const after = text.substring(end)

  textarea.value = before + shortcut + after

  const newPosition = start + shortcut.length
  textarea.setSelectionRange(newPosition, newPosition)
  textarea.focus()

  updateMathPreview(modalId)
}

/**
 * Initialize drawing canvas for math modal
 * @param {string} modalId - The ID of the modal element
 * @param {string} canvasId - The ID of the canvas element
 */
function initMathDrawingCanvas(modalId, canvasId) {
  if (mathDrawingInstances.has(modalId)) {
    return mathDrawingInstances.get(modalId)
  }

  const modal = document.getElementById(modalId)
  const canvas = document.getElementById(canvasId)
  if (!modal || !canvas) return null

  const ctx = canvas.getContext('2d')
  let isDrawing = false
  let currentStroke = null
  let strokes = []
  let strokeIdCounter = 0
  let undoStack = []
  let redoStack = []
  let onStateSaved = null

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.strokeStyle = '#000000'
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  function saveState() {
    undoStack.push(JSON.parse(JSON.stringify(strokes)))
    redoStack = []
    if (onStateSaved) {
      onStateSaved()
    }
  }

  function redrawCanvas() {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    strokes.forEach((stroke) => {
      if (stroke.x.length === 0) return
      ctx.beginPath()
      ctx.moveTo(stroke.x[0], stroke.y[0])
      for (let i = 1; i < stroke.x.length; i++) {
        ctx.lineTo(stroke.x[i], stroke.y[i])
      }
      ctx.stroke()
    })
  }

  function getCanvasScale() {
    const rect = canvas.getBoundingClientRect()
    return {
      scaleX: canvas.width / rect.width,
      scaleY: canvas.height / rect.height,
    }
  }

  function startDrawing(e) {
    isDrawing = true
    const rect = canvas.getBoundingClientRect()
    const scale = getCanvasScale()
    const clientX = e.clientX || (e.touches && e.touches[0].clientX)
    const clientY = e.clientY || (e.touches && e.touches[0].clientY)

    const x = (clientX - rect.left) * scale.scaleX
    const y = (clientY - rect.top) * scale.scaleY
    const t = Date.now()

    currentStroke = {
      id: `stroke_${strokeIdCounter++}`,
      x: [Math.round(x)],
      y: [Math.round(y)],
      t: [t],
      p: [0.5],
      pointerType: 'PEN',
      pointerId: 0,
    }

    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  function draw(e) {
    if (!isDrawing || !currentStroke) return
    const rect = canvas.getBoundingClientRect()
    const scale = getCanvasScale()
    const clientX = e.clientX || (e.touches && e.touches[0].clientX)
    const clientY = e.clientY || (e.touches && e.touches[0].clientY)

    const x = (clientX - rect.left) * scale.scaleX
    const y = (clientY - rect.top) * scale.scaleY
    const t = Date.now()

    currentStroke.x.push(Math.round(x))
    currentStroke.y.push(Math.round(y))
    currentStroke.t.push(t)
    currentStroke.p.push(0.5)

    ctx.lineTo(x, y)
    ctx.stroke()
  }

  function stopDrawing() {
    if (isDrawing && currentStroke) {
      strokes.push(currentStroke)
      saveState()
      currentStroke = null
      resetRecognitionState(modalId)
    }
    isDrawing = false
  }

  canvas.addEventListener('mousedown', startDrawing)
  canvas.addEventListener('mousemove', draw)
  canvas.addEventListener('mouseup', stopDrawing)
  canvas.addEventListener('mouseleave', stopDrawing)
  canvas.addEventListener('touchstart', (e) => {
    e.preventDefault()
    startDrawing(e)
  })
  canvas.addEventListener('touchmove', (e) => {
    e.preventDefault()
    draw(e)
  })
  canvas.addEventListener('touchend', (e) => {
    e.preventDefault()
    stopDrawing()
  })

  function undo() {
    if (undoStack.length === 0) return

    redoStack.push(JSON.parse(JSON.stringify(strokes)))
    strokes = undoStack.pop()
    redrawCanvas()
    resetRecognitionState(modalId)
  }

  function redo() {
    if (redoStack.length === 0) return

    undoStack.push(JSON.parse(JSON.stringify(strokes)))
    strokes = redoStack.pop()
    redrawCanvas()
    resetRecognitionState(modalId)
  }

  function clearCanvas() {
    saveState()
    strokes = []
    strokeIdCounter = 0
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  function resetDrawing() {
    strokes = []
    strokeIdCounter = 0
    undoStack = []
    redoStack = []
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  const instance = {
    clearCanvas,
    resetDrawing,
    undo,
    redo,
    getStrokes: () => strokes,
    canUndo: () => undoStack.length > 0,
    canRedo: () => redoStack.length > 0,
    setOnStateSaved: (callback) => {
      onStateSaved = callback
    },
  }
  mathDrawingInstances.set(modalId, instance)
  return instance
}

/**
 * Reset recognition state when canvas changes
 * @param {string} modalId - The ID of the modal element
 */
function resetRecognitionState(modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  const activeTab = modal.querySelector('.math-modal-tab.math-modal-tab-active')
  const activeTabName = activeTab ? activeTab.getAttribute('data-tab') : 'latex'

  if (activeTabName !== 'drawing') return
  if (!recognizedLatex.has(modalId)) return

  recognizedLatex.delete(modalId)

  const toolbarInstanceId = modalId.replace('math_modal_', '')
  const previewId = `math_drawing_preview_${toolbarInstanceId}`
  const preview = document.getElementById(previewId)
  if (preview) {
    preview.innerHTML =
      '<div class="math-modal-preview-placeholder">Draw math and click "Recognize" to see preview...</div>'
  }

  const recognizeButton = modal.querySelector(
    '[data-action="recognizeMathDrawing"]'
  )
  const insertButton = modal.querySelector(
    '[data-action="insertMathFromModal"]'
  )
  if (recognizeButton)
    recognizeButton.classList.remove('math-modal-button-hidden')
  if (insertButton) insertButton.classList.add('math-modal-button-hidden')
}

/**
 * Update drawing button states (undo/redo enabled/disabled)
 * @param {string} modalId - The ID of the modal element
 */
function updateDrawingButtonStates(modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  const instance = mathDrawingInstances.get(modalId)
  if (!instance) return

  const undoButton = modal.querySelector('[data-action="undoMathDrawing"]')
  const redoButton = modal.querySelector('[data-action="redoMathDrawing"]')

  if (undoButton) {
    const canUndo = instance.canUndo()
    undoButton.disabled = !canUndo
  }
  if (redoButton) {
    const canRedo = instance.canRedo()
    redoButton.disabled = !canRedo
  }
}

/**
 * Set up math modal event listeners
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} modalId - The ID of the modal element
 */
export function setupMathListeners(editor, modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  const toolbarInstanceId = modalId.replace('math_modal_', '')
  const canvasId = `math_drawing_canvas_${toolbarInstanceId}`

  const instance = initMathDrawingCanvas(modalId, canvasId)

  updateDrawingButtonStates(modalId)

  if (instance && instance.setOnStateSaved) {
    instance.setOnStateSaved(() => {
      updateDrawingButtonStates(modalId)
    })
  }

  if (instance) {
    const originalUndo = instance.undo
    const originalRedo = instance.redo
    instance.undo = function () {
      originalUndo()
      updateDrawingButtonStates(modalId)
    }
    instance.redo = function () {
      originalRedo()
      updateDrawingButtonStates(modalId)
    }
  }

  const tabs = modal.querySelectorAll('.math-modal-tab')
  const insertButton = modal.querySelector(
    '[data-action="insertMathFromModal"]'
  )

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const tabName = tab.getAttribute('data-tab')

      tabs.forEach((t) => t.classList.remove('math-modal-tab-active'))
      tab.classList.add('math-modal-tab-active')

      const tabContents = modal.querySelectorAll('.math-modal-tab-content')
      tabContents.forEach((content) => {
        const contentTab = content.getAttribute('data-tab-content')
        if (contentTab === tabName) {
          content.classList.add('math-modal-tab-content-active')
        } else {
          content.classList.remove('math-modal-tab-content-active')
        }
      })
      const recognizeButton = modal.querySelector(
        '[data-action="recognizeMathDrawing"]'
      )
      if (tabName === 'drawing') {
        if (recognizeButton)
          recognizeButton.classList.remove('math-modal-button-hidden')
        if (insertButton) insertButton.classList.add('math-modal-button-hidden')
      } else {
        if (recognizeButton)
          recognizeButton.classList.add('math-modal-button-hidden')
        if (insertButton) {
          insertButton.classList.remove('math-modal-button-hidden')
          insertButton.textContent = 'Insert'
        }
      }
    })
  })

  modal.addEventListener('click', (e) => {
    const button = e.target.closest('button')
    if (!button) return

    const action = button.getAttribute('data-action')
    if (!action) return

    e.preventDefault()
    e.stopPropagation()

    if (action === 'closeMathModal' || action === 'cancelMathModal') {
      hideMathModal(modalId)
    } else if (action === 'insertMathFromModal') {
      insertMathFromModal(editor, modalId)
    } else if (action === 'recognizeMathDrawing') {
      recognizeMathDrawing(editor, modalId)
    } else if (action === 'undoMathDrawing') {
      const instance = mathDrawingInstances.get(modalId)
      if (instance) {
        instance.undo()
        updateDrawingButtonStates(modalId)
      }
    } else if (action === 'redoMathDrawing') {
      const instance = mathDrawingInstances.get(modalId)
      if (instance) {
        instance.redo()
        updateDrawingButtonStates(modalId)
      }
    } else if (action === 'clearMathDrawing') {
      const instance = mathDrawingInstances.get(modalId)
      if (instance) {
        instance.clearCanvas()
        updateDrawingButtonStates(modalId)
        resetRecognitionState(modalId)
      }
    }
  })

  const overlay = modal.querySelector('.math-modal-overlay')
  if (overlay) {
    overlay.addEventListener('click', () => {
      hideMathModal(modalId)
    })
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) {
      hideMathModal(modalId)
    }
  })

  const textarea = modal.querySelector('textarea')
  if (textarea) {
    textarea.addEventListener('input', () => {
      updateMathPreview(modalId)
    })

    const radioName = `math_type_${modalId.replace('math_modal_', '')}`
    const radios = modal.querySelectorAll(
      `input[type="radio"][name="${radioName}"]`
    )
    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        updateMathPreview(modalId)
      })
    })

    textarea.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        insertMathFromModal(editor, modalId)
      }
    })
  }

  const shortcutButtons = modal.querySelectorAll('.math-shortcut-btn')
  shortcutButtons.forEach((button) => {
    button.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      const shortcut = button.getAttribute('data-shortcut')
      if (shortcut) {
        insertMathShortcut(modalId, shortcut)
      }
    })
  })
}
